import { V1Rule, V1RuleSet } from 'next-gen-sdk'

/**
 * Normalizes a rule by removing UI-specific properties for comparison
 */
export const normalizeRule = (rule: V1Rule | null | undefined): V1Rule => {
  // Handle null/undefined input
  if (!rule || typeof rule !== 'object') {
    return {
      id: '',
      term: '',
      type: 'term',
    }
  }

  const normalizedRule = { ...rule } as any
  delete normalizedRule.tempId
  return normalizedRule
}

/**
 * Normalizes a rule set by removing UI-specific properties and sorting arrays for comparison
 */
export const normalizeRuleSet = (
  ruleSet: V1RuleSet | null | undefined,
): V1RuleSet => {
  // Handle null/undefined input
  if (!ruleSet || typeof ruleSet !== 'object') {
    return {
      id: '',
      operator: 'and',
      rules: [],
    }
  }

  // Remove tempId property if it exists and create normalized copy
  const normalizedRuleSet = { ...ruleSet } as any
  delete normalizedRuleSet.tempId

  // Ensure we have default values
  normalizedRuleSet.id = normalizedRuleSet.id || ''
  normalizedRuleSet.operator = normalizedRuleSet.operator || 'and'

  // Ensure rules is an array
  if (!Array.isArray(normalizedRuleSet.rules)) {
    normalizedRuleSet.rules = []
  }

  // Sort rules by a stable property for consistent comparison
  if (normalizedRuleSet.rules.length > 0) {
    normalizedRuleSet.rules = normalizedRuleSet.rules
      .map(normalizeRule)
      .sort((a: V1Rule, b: V1Rule) => {
        // Sort by id first (existing rules), then by term for new rules
        if (a.id && b.id) return a.id.localeCompare(b.id)
        if (a.id && !b.id) return -1
        if (!a.id && b.id) return 1
        return (a.term || '').localeCompare(b.term || '')
      })
  }

  // Recursively normalize nested rule sets and sort them
  if (Array.isArray(normalizedRuleSet.ruleSet)) {
    normalizedRuleSet.ruleSet = normalizedRuleSet.ruleSet
      .map(normalizeRuleSet)
      .sort((a: V1RuleSet, b: V1RuleSet) => {
        // Sort by id first (existing rule sets), then by operator
        if (a.id && b.id) return a.id.localeCompare(b.id)
        if (a.id && !b.id) return -1
        if (!a.id && b.id) return 1
        return (a.operator || '').localeCompare(b.operator || '')
      })
  } else if (normalizedRuleSet.ruleSet !== undefined) {
    // If ruleSet exists but is not an array, set it to empty array
    normalizedRuleSet.ruleSet = []
  }

  return normalizedRuleSet
}

/**
 * Deep clones and normalizes a rule set for safe comparison
 */
export const cloneAndNormalizeRuleSet = (
  ruleSet: V1RuleSet | undefined,
): V1RuleSet | undefined => {
  if (!ruleSet) return undefined
  try {
    return normalizeRuleSet(JSON.parse(JSON.stringify(ruleSet)))
  } catch {
    return normalizeRuleSet(ruleSet)
  }
}

/**
 * Deep clones and normalizes a rule for safe comparison
 */
export const cloneAndNormalizeRule = (
  rule: V1Rule | undefined,
): V1Rule | undefined => {
  if (!rule) return undefined
  try {
    return normalizeRule(JSON.parse(JSON.stringify(rule)))
  } catch {
    return normalizeRule(rule)
  }
}
